import { useCombobox, type UseComboboxState } from "downshift";
import { useContext, useCallback, useEffect, useRef, useState, type Ref, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Form } from "react-bootstrap";
import type { ValueLabel } from "../types/ValueLabel";
import ValidationContext from "./ValidationContext";

const useMergedRef = <T,>(
  ref1: Ref<T> | undefined,
  ref2: Ref<T> | undefined
): Ref<T> => {
  return useCallback(
    (element: T) => {
      if (ref1) {
        if (typeof ref1 === "function") {
          ref1(element);
        } else {
          ref1.current = element;
        }
      }
      if (ref2) {
        if (typeof ref2 === "function") {
          ref2(element);
        } else {
          ref2.current = element;
        }
      }
    },
    [ref1, ref2]
  );
};

interface Props<T extends string | number = string> {
  options: ValueLabel<T, string>[];
  value: T | ValueLabel<T, string> | null | undefined;
  label?: ReactNode;
  placeholder?: string;
  noResultsText?: string;
  disabled?: boolean;
  required?: boolean;
  helpTextAccordion?: ReactNode;
  clearable?: boolean;
  fieldName?: string;
  showValidation?: boolean;
  setValue: (v: ValueLabel<T, string> | null | undefined) => void;
}

/**
 * Filters items based on input value and selection state.
 * Behavior similar to react-select createFilter with ignoreAccents: false, matchFrom: "any"
 *
 * - Shows all items if input matches the selected item's label (for re-selection)
 * - Otherwise filters by substring match (case-insensitive, anywhere in the label)
 * - Preserves accents and diacritics (no normalization)
 */
const getFilteredItems = <T extends string | number>(
  items: ValueLabel<T, string>[],
  inputValue: string,
  valueLabel: string | undefined,
): ValueLabel<T, string>[] => {
  const isInputMatchingSelection =
    valueLabel && inputValue === valueLabel?.toString?.();

  if (isInputMatchingSelection) {
    return items;
  }

  if (!inputValue) {
    return items;
  }

  const lowerInputValue = inputValue.toLowerCase();

  return items.filter((item) =>
    item.label?.toString?.().toLowerCase().includes(lowerInputValue),
  );
};

export default function LargeSelectInputCombobox<
  T extends string | number = string,
>({
  options,
  value,
  label,
  placeholder,
  noResultsText,
  disabled = false,
  required = false,
  helpTextAccordion,
  clearable = false,
  fieldName,
  showValidation = false,
  setValue,
}: Readonly<Props<T>>) {
  const { t } = useTranslation();
  const { suppressFieldErrors } = useContext(ValidationContext);
  const [items, setItems] = useState<ValueLabel<T, string>[]>([]);
  const [inputValue, setInputValue] = useState<string>("");
  const [liveMessage, setLiveMessage] = useState<string>("");

  // Refs for Downshift - input and menu needed for useCombobox
  const menuRef = useRef<HTMLUListElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const resolveValue = (
    v: T | ValueLabel<T, string> | null | undefined,
  ): T | null | undefined => {
    if (v === null || v === undefined) return v;
    if (typeof v === "object" && "value" in v) {
      return (v as ValueLabel<T, string>).value;
    }
    return v as T;
  };

  const resolvedValue = resolveValue(value);
  const selectedItem =
    resolvedValue !== null
      ? options.find((o) => o.value === resolvedValue) || null
      : null;
  const helpTextId = fieldName ? `${fieldName}-help` : undefined;
  const labelId = fieldName ? `${fieldName}-label` : undefined;
  const errorId = fieldName ? `${fieldName}-error` : undefined;
  const isInvalid = showValidation && required && !resolvedValue;
  const ariaDescribedBy = [
    helpTextAccordion && helpTextId ? helpTextId : undefined,
    isInvalid && errorId && !suppressFieldErrors ? errorId : undefined,
  ]
    .filter(Boolean)
    .join(" ");

  useEffect(() => {
    setItems(options);
  }, [options]);

  const filteredItems = getFilteredItems(
    items,
    inputValue,
    selectedItem?.label,
  );

  const getA11yStatusMessage = ({
    isOpen,
  }: UseComboboxState<ValueLabel<T, string>>) => {
    if (!isOpen) return "";
    const resultCount = filteredItems.length;
    if (resultCount === 0) return t("largeSelectInput.a11y.noResults");
    const resultsText = t("largeSelectInput.a11y.results", {
      count: resultCount,
    });
    return t("largeSelectInput.a11y.status", { resultsText });
  };

  const {
    isOpen,
    getLabelProps,
    getMenuProps,
    getInputProps,
    highlightedIndex,
    getItemProps,
    selectedItem: downshiftSelectedItem,
  } = useCombobox<ValueLabel<T, string>>({
    items: filteredItems,
    selectedItem: selectedItem,
    inputValue,
    onInputValueChange: ({ inputValue: newInputValue = "" }) => {
      setInputValue(newInputValue);
    },
    onSelectedItemChange: ({ selectedItem: newSelectedItem }) => {
      setValue(newSelectedItem ?? null);
      setInputValue(newSelectedItem?.label?.toString?.() ?? "");
      if (newSelectedItem) {
        setLiveMessage(
          t("largeSelectInput.a11y.selected", {
            item: newSelectedItem.label?.toString?.() ?? "",
          }),
        );
      }
    },
    itemToString: (item) => (item ? (item.label?.toString?.() ?? "") : ""),
    getA11yStatusMessage: getA11yStatusMessage,
  });

  // Merge Downshift props with refs for proper attachment
  const labelProps = getLabelProps({ id: labelId });
  
  // Get props and merge Downshift's internal ref with our ref
  const inputPropsBase = getInputProps({ "aria-labelledby": labelId });
  const mergedInputRef = useMergedRef((inputPropsBase as any).ref, inputRef);
  
  const menuPropsBase = getMenuProps();
  const mergedMenuRef = useMergedRef((menuPropsBase as any).ref, menuRef);
  
  const inputProps = { ...inputPropsBase, ref: mergedInputRef };
  const menuProps = { ...menuPropsBase, ref: mergedMenuRef };

  return (
    <Form.Group className="form-group">
      <div className="large-select-input-wrapper">
        <label {...labelProps}>{label}</label>
        {helpTextAccordion}
        <div className="large-select-input-container">
          <input
            {...inputProps}
            id={fieldName}
            placeholder={placeholder ?? t("defaultSelectPlaceholder")}
            disabled={disabled}
            required={required}
            className="large-select-input"
            aria-describedby={ariaDescribedBy || undefined}
            autoCorrect="off"
            spellCheck="false"
          />
          {resolvedValue !== null && !disabled && clearable && (
            <button
              type="button"
              className="large-select-clear"
              onClick={() => {
                const removedMessage = t("largeSelectInput.a11y.removed", {
                  item: selectedItem?.label?.toString?.() ?? "",
                });
                setLiveMessage(removedMessage);
                setValue(null);
                setInputValue("");
              }}
              aria-label={t("largeSelectInput.clearSelection")}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="4" y1="4" x2="12" y2="12"></line>
                <line x1="12" y1="4" x2="4" y2="12"></line>
              </svg>
            </button>
          )}
          <span className="large-select-chevron">
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="4 6 8 10 12 6"></polyline>
            </svg>
          </span>
        </div>
        <ul
          {...menuProps}
          className="large-select-menu"
          style={isOpen ? {} : { display: "none" }}
        >
          {isOpen &&
            (filteredItems.length > 0 ? (
              filteredItems.map((item, index) => (
                <li
                  key={`${item.value}`}
                  {...getItemProps({
                    index,
                    item,
                  })}
                  className={`large-select-item ${
                    downshiftSelectedItem?.value === item.value
                      ? "is-selected"
                      : ""
                  } ${highlightedIndex === index ? "is-highlighted" : ""}`}
                >
                  {item.label?.toString() ?? String(item.label)}
                </li>
              ))
            ) : (
              <li className="large-select-item-no-results">
                {noResultsText ?? t("largeSelectInput.a11y.noResults")}
              </li>
            ))}
        </ul>
        <div className="sr-only" aria-live="polite">
          {liveMessage}
        </div>
      </div>
      {/* Validation workaround for LargeSelectInput */}
      {fieldName && (
        <>
          <Form.Control
            id={fieldName}
            value={
              typeof value === "object" && value !== null && "value" in value
                ? value.value || ""
                : resolvedValue || ""
            }
            style={{ display: "none" }}
            required={required}
            aria-hidden="true"
            onChange={() => {}}
            tabIndex={-1}
          />
          {isInvalid && (
            <Form.Control.Feedback
              id={errorId}
              type="invalid"
              aria-hidden={suppressFieldErrors || undefined}
              style={{ display: "block" }}
            >
              {t(`${fieldName}.invalid`)}
            </Form.Control.Feedback>
          )}
        </>
      )}
    </Form.Group>
  );
}
